import { useCallback, useEffect, useState } from 'react'
import { type LoginResponse } from '../api/auth'
import { ApiError } from '../api/client'
import {
  supplierInvoicesApi, taxApi,
  type SupplierInvoiceList, type TaxSettingsBundle,
} from '../api/supplierInvoices'
import { Button, Callout, Card, CardContent, CardHeader, DataTable, EmptyState, FilterBar, FormGrid, LoadingState, Page, PageHeader, Select, StatusPill, TextField } from '../components/ui'

/**
 * R5-07 (Z-17, Z-23): fatture dei fornitori lette da fatturazione.equalstudio.it (sola lettura, non sono
 * spese) e regole d'imposta modificabili dall'Admin, marcate «da confermare col commercialista».
 */
type Props = { auth: LoginResponse }

const eur = (n: number) => '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const day = (s?: string | null) => (s ? new Date(s).toLocaleDateString('it-IT') : '—')
const isAdmin = (a: LoginResponse) => a.role === 'Administrator' || a.role === 'EqualAdmin'
const msg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : 'Operazione non riuscita.')

export function SupplierInvoicesPage({ auth }: Props) {
  const [list, setList] = useState<SupplierInvoiceList | null>(null)
  const [q, setQ] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const p: { from?: string; to?: string; q?: string } = {}
      if (from) p.from = from
      if (to) p.to = to
      if (q.trim()) p.q = q.trim()
      setList(await supplierInvoicesApi.list(p))
    } catch (e) {
      setList(null)
      setError(e instanceof ApiError && e.status === 404 ? 'La lettura delle fatture dei fornitori non è ancora attiva.' : msg(e))
    } finally {
      setLoading(false)
    }
  }, [from, to, q])

  useEffect(() => { void load() }, [load])

  const sync = async () => {
    setSyncing(true)
    setInfo(null)
    try {
      const r = await supplierInvoicesApi.sync()
      setInfo(r.available ? `Lette ${r.fetched} fatture: ${r.created} nuove, ${r.updated} aggiornate.` : (r.message ?? 'Fatturazione non disponibile.'))
      await load()
    } catch (e) {
      setError(msg(e))
    } finally {
      setSyncing(false)
    }
  }

  return (
    <Page>
      <PageHeader
        title="Fatture fornitori"
        icon="inventory"
        actions={<Button variant="outlined" onClick={() => void sync()} disabled={syncing}>{syncing ? 'Lettura in corso…' : 'Rileggi da fatturazione'}</Button>}
      />
      <Callout tone="info">Le fatture arrivano da fatturazione.equalstudio.it (SDI): qui sono solo lette, non diventano spese e non vengono duplicate.</Callout>
      {info && <Callout tone="info">{info}</Callout>}
      {error && <Callout tone="error">{error}</Callout>}

      <FilterBar>
        <TextField label="Cerca fornitore o numero" value={q} onChange={e => setQ(e.target.value)} />
        <TextField label="Dal" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <TextField label="Al" type="date" value={to} onChange={e => setTo(e.target.value)} />
      </FilterBar>

      {loading && !list && <LoadingState />}
      {list && (
        <Card variant="outlined" padding="none">
          {list.items.length === 0 ? (
            <EmptyState icon="inventory" title="Nessuna fattura" description="Usa «Rileggi da fatturazione» per importare le fatture ricevute." />
          ) : (
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Data</th><th>Numero</th><th>Fornitore</th><th>Stato</th><th>Scadenza</th>
                  <th className="text-end">Imponibile</th><th className="text-end">IVA</th><th className="text-end">Totale</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map(i => (
                  <tr key={i.id}>
                    <td>{day(i.issueDate)}</td>
                    <td>{i.number}</td>
                    <td>{i.supplierName}{i.supplierId ? '' : <span className="text-muted"> (non in anagrafica)</span>}</td>
                    <td><StatusPill tone="neutral">{i.status || '—'}</StatusPill></td>
                    <td>{day(i.dueDate)}</td>
                    <td className="text-end text-nowrap md-num">{eur(i.netAmount)}</td>
                    <td className="text-end text-nowrap md-num">{eur(i.vatAmount)}</td>
                    <td className="text-end text-nowrap md-num">{eur(i.totalAmount)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={5}><strong>Totale ({list.total} fatture)</strong></td>
                  <td className="text-end text-nowrap md-num">{eur(list.sums.net)}</td>
                  <td className="text-end text-nowrap md-num">{eur(list.sums.vat)}</td>
                  <td className="text-end text-nowrap md-num">{eur(list.sums.total)}</td>
                </tr>
              </tbody>
            </DataTable>
          )}
        </Card>
      )}

      <TaxSection admin={isAdmin(auth)} />
    </Page>
  )
}

function TaxSection({ admin }: { admin: boolean }) {
  const [data, setData] = useState<TaxSettingsBundle | null>(null)
  const [form, setForm] = useState({ ires: '', irap: '', periodicity: 'trimestrale', advance: '', first: '' })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const apply = (d: TaxSettingsBundle) => {
    setData(d)
    setForm({
      ires: String(d.settings.iresRate), irap: String(d.settings.irapRate), periodicity: d.settings.vatPeriodicity,
      advance: String(d.settings.advancePercent), first: String(d.settings.firstAdvanceShare),
    })
  }
  const reload = useCallback(async () => {
    try { apply(await taxApi.get()); setError(null) } catch (e) { setError(msg(e)) }
  }, [])
  useEffect(() => { void reload() }, [reload])

  const num = (s: string) => Number(s.replace(',', '.'))
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try { await fn(); await reload() } catch (e) { setError(msg(e)) } finally { setBusy(false) }
  }

  if (!data) return error ? <Callout tone="error">{error}</Callout> : <LoadingState />
  return (
    <Card variant="outlined">
      <CardHeader title="Regole d'imposta" />
      <CardContent>
        {data.settings.toConfirm && (
          <Callout tone="warning">Da confermare col commercialista: i valori sono quelli di default (SRLS, regime ordinario, IVA trimestrale).</Callout>
        )}
        {!data.settings.toConfirm && <Callout tone="success">Confermate col commercialista il {day(data.settings.confirmedAt)}.</Callout>}
        {error && <Callout tone="error">{error}</Callout>}
        <FormGrid>
          <TextField label="IRES %" value={form.ires} disabled={!admin} onChange={e => setForm({ ...form, ires: e.target.value })} />
          <TextField label="IRAP %" value={form.irap} disabled={!admin} onChange={e => setForm({ ...form, irap: e.target.value })} />
          <Select label="Liquidazione IVA" value={form.periodicity} disabled={!admin} onChange={e => setForm({ ...form, periodicity: e.target.value })}>
            <option value="trimestrale">Trimestrale</option>
            <option value="mensile">Mensile</option>
          </Select>
          <TextField label="Acconto IRES/IRAP % dell'imposta" value={form.advance} disabled={!admin} onChange={e => setForm({ ...form, advance: e.target.value })} />
          <TextField label="Quota del primo acconto %" value={form.first} disabled={!admin} onChange={e => setForm({ ...form, first: e.target.value })} />
        </FormGrid>
        {admin ? (
          <div className="mt-3 d-flex gap-2">
            <Button variant="filled" disabled={busy} onClick={() => void run(() => taxApi.saveSettings({
              iresRate: num(form.ires), irapRate: num(form.irap), vatPeriodicity: form.periodicity,
              advancePercent: num(form.advance), firstAdvanceShare: num(form.first),
            }))}>Salva regole</Button>
            <Button variant="outlined" disabled={busy || !data.settings.toConfirm} onClick={() => void run(() => taxApi.confirm())}>Confermato dal commercialista</Button>
          </div>
        ) : <p className="text-muted mt-2">Solo l'amministratore può modificare le regole.</p>}

        <h3 className="md-typescale-title-small mt-4">Scadenze</h3>
        <DataTable variant="plain">
          <thead><tr><th>Scadenza</th><th>Giorno</th><th>Mese</th><th>Attiva</th>{admin && <th />}</tr></thead>
          <tbody>
            {data.deadlines.map(d => <DeadlineRow key={d.id + ':' + d.day + ':' + d.month + ':' + d.isActive} d={d} admin={admin} busy={busy} onSave={(b) => run(() => taxApi.saveDeadline(d.id, b))} />)}
          </tbody>
        </DataTable>
      </CardContent>
    </Card>
  )
}

function DeadlineRow({ d, admin, busy, onSave }: {
  d: TaxSettingsBundle['deadlines'][number]; admin: boolean; busy: boolean
  onSave: (b: { month: number; day: number; isActive: boolean }) => Promise<void>
}) {
  const [dayV, setDayV] = useState(String(d.day))
  const [monthV, setMonthV] = useState(String(d.month))
  const [active, setActive] = useState(d.isActive)
  return (
    <tr>
      <td>{d.label}</td>
      <td><input aria-label="Giorno" type="number" min={1} max={31} value={dayV} disabled={!admin} onChange={e => setDayV(e.target.value)} style={{ width: 64 }} /></td>
      <td><input aria-label="Mese" type="number" min={1} max={12} value={monthV} disabled={!admin} onChange={e => setMonthV(e.target.value)} style={{ width: 64 }} /></td>
      <td><input aria-label="Attiva" type="checkbox" checked={active} disabled={!admin} onChange={e => setActive(e.target.checked)} /></td>
      {admin && <td><Button variant="text" disabled={busy} onClick={() => void onSave({ month: Number(monthV), day: Number(dayV), isActive: active })}>Salva</Button></td>}
    </tr>
  )
}
